/**
 * TemplateCategoryBadge - Category indicator for template types
 *
 * Displays template category with semantic color coding:
 * - email: indigo (email templates)
 * - sms: purple (SMS templates)
 * - document: blue (document templates)
 * - form: emerald (form templates)
 */
import { Mail, MessageSquare, FileText, ClipboardList } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { TemplateCategory } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface TemplateCategoryBadgeProps {
  category: TemplateCategory;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// Configuration
// ============================================

const categoryConfig: Record<
  TemplateCategory,
  { label: string; icon: typeof Mail; dotColor: string; badgeClass: string }
> = {
  email: {
    label: 'Email',
    icon: Mail,
    dotColor: 'bg-indigo-500',
    badgeClass:
      'bg-indigo-500/10 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 border-indigo-500/20',
  },
  sms: {
    label: 'SMS',
    icon: MessageSquare,
    dotColor: 'bg-purple-500',
    badgeClass:
      'bg-purple-500/10 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400 border-purple-500/20',
  },
  document: {
    label: 'Document',
    icon: FileText,
    dotColor: 'bg-blue-500',
    badgeClass:
      'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20',
  },
  form: {
    label: 'Form',
    icon: ClipboardList,
    dotColor: 'bg-emerald-500',
    badgeClass:
      'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

// ============================================
// Component
// ============================================

export function TemplateCategoryBadge({
  category,
  showIcon = true,
  size = 'md',
  className,
}: TemplateCategoryBadgeProps) {
  const config = categoryConfig[category];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        sizeClasses[size],
        config.badgeClass,
        className
      )}
    >
      {showIcon && <Icon className={iconSizeClasses[size]} />}
      {config.label}
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

export function getCategoryColor(category: TemplateCategory): string {
  const colors: Record<TemplateCategory, string> = {
    email: '#6366f1',
    sms: '#a855f7',
    document: '#3b82f6',
    form: '#10b981',
  };
  return colors[category];
}

export function getCategoryLabel(category: TemplateCategory): string {
  return categoryConfig[category].label;
}

export function getCategoryIcon(category: TemplateCategory) {
  return categoryConfig[category].icon;
}
